// 🏷️ AdresEtiketModal — printklare A4-pdf met adresetiketten (210 × 148 mm, 2 per blad) voor leverbonnen.
import React, { useEffect, useMemo, useState } from 'react';
import { X, Tag, FilePdf, DownloadSimple } from '@phosphor-icons/react';
import API from '@/lib/api';
import { fetchPdfActions, openPdf, downloadPdf } from '@/lib/pdfActions';
import { isIOS } from '@/lib/platform';
import { toast } from 'sonner';

const KEUZES = [1, 2, 4, 6, 8, 10];

export default function AdresEtiketModal({ docIds, onClose }) {
  const ids = useMemo(() => (Array.isArray(docIds) ? docIds : [docIds]), [docIds]);
  const isBulk = ids.length > 1;
  const [aantal, setAantal] = useState(2);
  const [voet, setVoet] = useState(() => localStorage.getItem('etiket_voet') === '1');
  const [colli, setColli] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => { localStorage.setItem('etiket_voet', voet ? '1' : '0'); }, [voet]);

  useEffect(() => {
    if (isBulk) return;
    API.get(`/documenten/${ids[0]}/adresetiketten/preview`).then(r => setPreview(r.data)).catch(() => setPreview(null));
  }, [ids, isBulk]);

  const totaal = ids.length * aantal;
  const bladen = Math.ceil(totaal / 2);
  const voetTekst = [voet ? (isBulk ? 'LB-nummer' : ids[0]) : '', colli > 0 ? `Colli 1/${colli}` : ''].filter(Boolean).join(' · ');

  const wijzigColli = (v) => {
    const c = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    setColli(c);
    if (c > 0) setAantal(c);
  };

  const maak = async (actie) => {
    setBusy(actie);
    try {
      const label = isBulk ? `Adresetiketten ${ids.length} leverbonnen` : `Adresetiketten ${ids[0]}`;
      const opties = { aantal, voet: voet ? 1 : 0, colli };
      const actions = isBulk
        ? await fetchPdfActions({ endpoint: '/leverbonnen/adresetiketten', method: 'POST', body: { doc_ids: ids, ...opties }, label })
        : await fetchPdfActions({ endpoint: `/documenten/${ids[0]}/adresetiketten`, params: opties, label });
      if (actie === 'download' && !isIOS()) downloadPdf(actions); else openPdf(actions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Etiketten maken mislukt');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="adres-etiket-modal">
        <div className="bg-[#1a1a1a] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag size={20} weight="fill" />
            <div>
              <div className="font-bold text-sm">{isBulk ? `Adresetiketten voor ${ids.length} leverbonnen` : 'Adresetiketten'}</div>
              <div className="text-[11px] text-white/70">A4 staand · 2 etiketten van 210 × 148 mm per blad · afdrukken op 100%</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/15 rounded" data-testid="adres-etiket-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {!isBulk && (
            <div className="border border-gray-200 bg-gray-50 px-4 py-5 text-center leading-snug" data-testid="adres-etiket-preview">
              {preview ? (<>
                <div className="font-black text-xl text-gray-900">{preview.naam}</div>
                {preview.straat && <div className="text-sm text-gray-800 mt-1">{preview.straat}</div>}
                {preview.plaats && <div className="font-bold text-base text-gray-900">{preview.plaats}</div>}
                {preview.land && <div className="text-sm text-gray-700 mt-1">{preview.land}</div>}
                {preview.btw && <div className="text-sm text-gray-700">{preview.btw}</div>}
                {voetTekst && <div className="text-[11px] text-gray-600 mt-3" data-testid="adres-etiket-preview-voet">{voetTekst}</div>}
              </>) : <div className="text-xs text-gray-400">Adres laden…</div>}
            </div>
          )}

          <div className="flex items-start gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none" data-testid="adres-etiket-voet-toggle">
              <input type="checkbox" checked={voet} onChange={e => setVoet(e.target.checked)} className="w-4 h-4 accent-[#002FA7]" data-testid="adres-etiket-voet-checkbox" />
              Leverbonnummer klein onderaan
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              Aantal colli
              <input type="number" min={0} max={100} value={colli || ''} placeholder="—" onChange={e => wijzigColli(e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" data-testid="adres-etiket-colli-input" title="Nummert elk etiket als Colli 1/N … N/N (0 of leeg = niet tonen)" />
            </label>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Aantal etiketten {isBulk ? 'per leverbon' : ''}</label>
            <div className="flex flex-wrap gap-1.5">
              {KEUZES.map(n => (
                <button key={n} type="button" onClick={() => setAantal(n)}
                  className={`px-3 py-1.5 text-sm font-bold border rounded ${aantal === n ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`adres-etiket-aantal-${n}`}>{n}</button>
              ))}
              <input type="number" min={1} max={100} value={aantal}
                onChange={e => setAantal(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
                className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded" data-testid="adres-etiket-aantal-input" />
            </div>
            <div className="text-xs text-gray-500 mt-1.5" data-testid="adres-etiket-samenvatting">
              {totaal} etiket{totaal === 1 ? '' : 'ten'} · {bladen} A4-blad{bladen === 1 ? '' : 'en'}{colli > 0 ? ` · genummerd Colli 1/${colli} … ${colli}/${colli}` : ''}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button className="btn btn-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5" disabled={!!busy} onClick={() => maak('open')} data-testid="adres-etiket-open-btn">
            <FilePdf size={14} weight="bold" className="text-[#E02424]" /> {busy === 'open' ? 'Bezig…' : 'Bekijken'}
          </button>
          <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5" disabled={!!busy} onClick={() => maak('download')} data-testid="adres-etiket-download-btn">
            <DownloadSimple size={14} weight="bold" /> {busy === 'download' ? 'Bezig…' : 'PDF downloaden'}
          </button>
        </div>
      </div>
    </div>
  );
}
